import {
  CustomButton,
  CustomCheckbox,
  CustomCollapseCard,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomMaskInput,
  CustomSelect,
  CustomTooltip,
  EditableCell,
  Option,
  successDialog,
  Text,
} from '@src/components';
import DeliveryAddressUpdateModal from './DeliveryAddressUpdateModal';
import EacContactUpdateModal from './EacContactUpdateModal';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '@src/styles/manageMyCards/activeCardList.scss';
import '@src/styles/manageMyCards/eacSendConfirmationModal.scss';
import { Button, Form, Modal, Typography, Tooltip } from 'antd';
import { formMailRegex } from '@src/utils/formRule';
import { useDispatch, useSelector } from 'react-redux';
import {
  cancelRequestsDelete,
  cardGetPagedList,
  checkEacContactInfo,
  employeesUpdateList,
  sendEacCodeEmail,
  sendEacCodeSms,
  setSelectedCards,
} from '@src/store/slice/manageMyCardsSlice';
import BlockedModal from './BlockedModal';
import tickCircle from '@src/assets/icons/tick-circle.svg';
import iconCompAddress from '@src/assets/icons/location-comp.svg';
import iconAddress from '@src/assets/icons/icon-adres.svg';
import iconCancelCard from '@src/assets/icons/icon-kart-iptal.svg';
import iconChecked from '@src/assets/icons/icon-checked.svg';
import iconNoChecked from '@src/assets/icons/icon-no-check.svg';
import iconWarning from '@src/assets/icons/icon-dikkat.svg';
import MobilePaymentChangeModal from './MobilePaymentChangeModal';
import { handleAuthStatus } from '@src/utils/utils';
import { setIsAllSelectedRowKeys } from '@src/store/slice/balanceUploadSlice';
import { VirtualizedAndLazyScrollTable } from '@src/components/VirtualizedAndLazyScrollTable';
import CardFilter from './CardFilter';

import PropTypes from 'prop-types';
import { confirmDialog, errorDialog, warningDialog } from '@src/components/CustomDialog';
import { VouIdCode } from '@src/store/enums';
import CustomPagination from '@src/components/CustomPagination';
import { setLoadingState } from '@src/store/slice/appSettingsSlice';
import { useText } from '@src/components/Text';
import iconInfo from '@src/assets/icons/icon-bilgi.svg';
import eacInfoIcon from '@src/assets/icons/eac-info.svg';
import iconWhiteInfo from '@src/assets/icons/icn-bilgi-white.svg';
import { buildMobilePaymentDto } from './activeCardList/buildMobilePaymentDto';
import { buildEmployeeUpdateRequestDto } from './activeCardList/buildEmployeeUpdateRequestDto';
import { validateMobilePaymentGsm } from './activeCardList/validateMobilePaymentGsm';
import { isOnlyAddressChanged } from './activeCardList/isOnlyAddressChanged';
import { HasMobilePaymentComponent } from './activeCardList/HasMobilePaymentComponent';
import { HasMobileAppComponent } from './activeCardList/HasMobileAppComponent';
import { getInput } from './activeCardList/getInput';
import { GsmInputComponent } from './activeCardList/GsmInputComponent';
import { AddressIdComponent } from './activeCardList/AddressIdComponent';
import { mergeEacContactUpdates } from './activeCardList/mergeEacContactUpdates';

const isEacActionFailed = (action, thunk) =>
  !thunk.fulfilled.match(action) ||
  action.payload?.isSuccess === false ||
  action.payload?.isFailure;

const maskEacCardNumber = (card) => {
  const cardNumber = String(card?.cardNumber ?? '').replace(/\D/g, '');
  return `**** **** **** ${cardNumber.slice(-4)}`;
};

const getCardSelectionId = (card) => String(card?.cardNumber ?? card?.id ?? '');
const getCardRowKey = (card) => `manage-my-card-${getCardSelectionId(card)}`;

const ActiveCardList = ({ isCompliments = false, isMarket = false }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { list, tableProperty, filterObject, selectedCards } = useSelector(
    (state) => state?.manageMyCards,
  );
  const { addressPagedList } = useSelector((state) => state?.address);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const [editingKey, setEditingKey] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deliverAddressModalVisible, setDeliverAddressModalVisible] = useState(false);
  const [blockedModalVisible, setBlockedModalVisible] = useState(false);
  const [blockedModal2Visible, setBlockedModal2Visible] = useState(false);
  const [qrMobileChangeShow, setQrMobileChangeShow] = useState(false);
  const [eacContactCards, setEacContactCards] = useState([]);
  const [eacConfirmationCards, setEacConfirmationCards] = useState([]);
  const [activeCardListType, setActiveCardListType] = useState(2);
  const [complimentsList, setComplimentsList] = useState([]);
  const isComlimentsOrMarket = isCompliments || isMarket;
  const { userAuthorizationStatus, userAuthorizationGiftStatus, userAuthorizationMarketStatus } =
    useSelector((state) => state?.user);
  const sortRef = useRef({
    orderType: '',
    orderBy: '',
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
  });
  const isEditing = (record) => record.id === editingKey;

  const vouId = useMemo(() => {
    if (isMarket) return VouIdCode.Market;
    if (isCompliments) return VouIdCode.Compliments;
    return VouIdCode.Restaurant;
  }, [isMarket, isCompliments]);

  const userAuthorizationStatusForType = useMemo(() => {
    if (isMarket) return userAuthorizationMarketStatus;
    if (isCompliments) return userAuthorizationGiftStatus;
    return userAuthorizationStatus;
  }, [isMarket, isCompliments]);

  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const cardListText = useText('cardList');
  const phoneNumberFormat = useText('phoneNumberFormat');
  const pleaseSelectCard = useText('pleaseSelectCard');
  const eacConfirmTitle = useText('eacConfirmTitle');
  const eacConfirmSelectedLabel = useText('eacConfirmSelectedLabel');
  const eacConfirmSelectedCards = useText('eacConfirmSelectedCards');
  const eacConfirmDescriptionSingular = useText('eacConfirmDescriptionSingular');
  const eacConfirmDescriptionPlural = useText('eacConfirmDescriptionPlural');
  const eacConfirmPrompt = useText('eacConfirmPrompt');
  const eacConfirmButton = useText('approve2');
  const cancelButton = useText('giveUp');
  const eacSendErrorTitle = useText('eacSendErrorTitle');
  const eacSendErrorMessage = useText('eacSendErrorMessage');
  const eacSendSuccessTitle = useText('eacSendSuccessTitle');
  const eacSendSuccessDescription = useText('eacSendSuccessDescription');

  const handleSendEac = useCallback(async (cardsToSend = selectedCards) => {
    if (!cardsToSend?.length) {
      warningDialog({
        title: <Text t="attention" />,
        message: pleaseSelectCard,
      });
      return;
    }

    const customerId = selectedCustomer?.customerId || cardsToSend[0]?.customerId;
    if (!customerId) {
      errorDialog({ title: eacSendErrorTitle, message: eacSendErrorMessage });
      return;
    }

    const requestData = {
      customerId,
      vouId,
      cardNumberList: cardsToSend.map((card) => ({ cardNumber: card.cardNumber })),
    };
    const contactInfoAction = await dispatch(checkEacContactInfo(requestData));
    if (isEacActionFailed(contactInfoAction, checkEacContactInfo)) {
      errorDialog({
        title: eacSendErrorTitle,
        message:
          contactInfoAction.payload?.message ||
          contactInfoAction.payload?.error?.message ||
          eacSendErrorMessage,
      });
      return;
    }

    const contactInfoData = contactInfoAction.payload?.data;
    const cardsWithoutContact = Array.isArray(contactInfoData)
      ? contactInfoData
      : contactInfoData?.items;
    if (!Array.isArray(cardsWithoutContact)) {
      errorDialog({ title: eacSendErrorTitle, message: eacSendErrorMessage });
      return;
    }

    if (cardsWithoutContact.length) {
      setEacContactCards(cardsWithoutContact);
      return;
    }

    setEacConfirmationCards(cardsToSend);
  }, [
    cancelButton,
    dispatch,
    eacSendErrorMessage,
    eacSendErrorTitle,
    eacSendSuccessDescription,
    eacSendSuccessTitle,
    filterObject,
    pleaseSelectCard,
    selectedCustomer,
    selectedCards,
    vouId,
  ]);

  const handleConfirmEacSend = useCallback(async () => {
    const cardsToSend = eacConfirmationCards;
    if (!cardsToSend.length) return;

    setEacConfirmationCards([]);
    const maskedCardNumbers = cardsToSend.map(maskEacCardNumber);
    const smsCards = cardsToSend.filter((card) => String(card?.gsm ?? '').trim());
    const emailCards = cardsToSend.filter(
      (card) => !String(card?.gsm ?? '').trim() && String(card?.email ?? '').trim(),
    );
    if (!smsCards.length && !emailCards.length) {
      errorDialog({ title: eacSendErrorTitle, message: eacSendErrorMessage });
      return;
    }

    const customerId = selectedCustomer?.customerId || cardsToSend[0]?.customerId;
    if (!customerId) {
      errorDialog({ title: eacSendErrorTitle, message: eacSendErrorMessage });
      return;
    }

    const sendRequest = { customerId, vouId };
    if (smsCards.length) {
      const smsAction = await dispatch(
        sendEacCodeSms({
          ...sendRequest,
          cardNumberList: smsCards.map((card) => ({ cardNumber: card.cardNumber })),
        }),
      );
      if (isEacActionFailed(smsAction, sendEacCodeSms)) {
        errorDialog({
          title: eacSendErrorTitle,
          message: smsAction.payload?.message || eacSendErrorMessage,
        });
        return;
      }
    }
    if (emailCards.length) {
      const emailAction = await dispatch(
        sendEacCodeEmail({
          ...sendRequest,
          cardNumberList: emailCards.map((card) => ({ cardNumber: card.cardNumber })),
        }),
      );
      if (isEacActionFailed(emailAction, sendEacCodeEmail)) {
        errorDialog({
          title: eacSendErrorTitle,
          message: emailAction.payload?.message || eacSendErrorMessage,
        });
        return;
      }
    }

    successDialog({
      title: eacSendSuccessTitle,
      htmlContent: (
        <div style={{ textAlign: 'center' }}>
          {maskedCardNumbers.map((cardNumber, index) => (
            <p key={`${cardNumber}-${index}`} style={{ margin: '0 0 8px' }}>
              <strong>{cardNumber}</strong>
            </p>
          ))}
          <p>{eacSendSuccessDescription}</p>
        </div>
      ),
      onOk: async () => {
        setSelectedRowKeys([]);
        dispatch(setSelectedCards([]));
        await dispatch(setIsAllSelectedRowKeys(false));
        await dispatch(cardGetPagedList({ body: { vouId }, ...filterObject }));
      },
    });
  }, [
    dispatch,
    eacConfirmationCards,
    eacSendErrorMessage,
    eacSendErrorTitle,
    eacSendSuccessDescription,
    eacSendSuccessTitle,
    filterObject,
    selectedCustomer,
    vouId,
  ]);

  useEffect(() => {
    const tempComplimentsList = list?.map((item, index) => {
      let newItem = { ...item };
      newItem.rowNumber = index + 1 + (pagination.current - 1) * pagination.pageSize;
      return newItem;
    });
    setComplimentsList(tempComplimentsList);
  }, [list, isCompliments, isMarket]);

  const handleTableChange = useCallback(
    async (pageNumber, pageSize) => {
      await dispatch(
        cardGetPagedList({
          body: { vouId: vouId },
          ...filterObject,
          pageNumber: isComlimentsOrMarket ? pagination.current : pageNumber,
          pageSize: isComlimentsOrMarket ? pagination.pageSize : pageSize,
          sortParams: sortRef.current,
        }),
      );

      handleGiveUp();
    },
    [dispatch, filterObject, pagination],
  );

  const handleTableSortedChange = useCallback(async (pagination, filters, sorter, allTable) => {
    let { order, field } = sorter;
    if (!order) {
      sortRef.current = {};
    } else {
      if (field === 'addressId') {
        field = 'addressName';
      }
      sortRef.current = {
        OrderType: order === 'ascend' ? 'asc' : 'desc',
        OrderBy: field,
      };
    }
    await handleTableChange(1, allTable?.currentDataSource.length || 30);
  }, []);

  const handleEdit = useCallback(
    (record) => {
      form.setFieldsValue({
        ...record,
      });

      setEditingKey(record.id);
    },
    [form],
  );

  const handleGiveUp = useCallback(() => {
    setEditingKey('');
    form.resetFields();
  }, [form]);

  const handleCancelProceed = useCallback(
    (record) => {
      dispatch(setSelectedCards([record]));
      setBlockedModalVisible(true);
    },
    [dispatch],
  );

  const handleUpdate = useCallback(
    async (record) => {
      try {
        const values = await form.validateFields();

        if (!validateMobilePaymentGsm({ record, values, form })) {
          return;
        }

        let body = {
          customerId: record?.customerId,
          vouId: vouId,
          requestDto: {
            ...buildEmployeeUpdateRequestDto({
              record,
              values,
              isAddressUpdate: isOnlyAddressChanged(record, values),
            }),
          },
          mobilePaymentUpdateRequestDto: buildMobilePaymentDto({
            record,
            values,
            vouId,
          }),
        };
        if (
          !body?.requestDto?.mobileAppUserId &&
          body?.mobilePaymentUpdateRequestDto?.hasMobilePayment &&
          !/^5\d{9}$/.test(body?.requestDto?.gsm?.replaceAll(/\D/g, ''))
        ) {
          form.setFields([
            {
              name: 'gsm',
              errors: [phoneNumberFormat],
            },
          ]);
          return;
        }

        if (!record.cardType) {
          body.mobilePaymentUpdateRequestDto.hasMobilePayment = values?.hasMobilePayment;
          body.mobilePaymentUpdateRequestDto.mobileAppUserId = null;
        }

        if (!values?.mobileAppUserId && record?.mobileAppUserId) {
          body.mobilePaymentUpdateRequestDto.hasMobilePayment = null;
          body.mobilePaymentUpdateRequestDto.mobileAppUserId = record?.mobileAppUserId;
        }

        const action = await dispatch(employeesUpdateList(body));
        if (employeesUpdateList.fulfilled.match(action)) {
          form.resetFields();
          setEditingKey('');
          successDialog({
            title: <Text t="successfullySent" />,
            message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
            onOk: async () => {
              await dispatch(
                cardGetPagedList({
                  body: { vouId: vouId },
                  ...filterObject,
                }),
              );
            },
          });
        }
      } catch (error) { }
    },
    [dispatch, filterObject, form],
  );

  const onSelectChange = useCallback(
    (sRowKeys, sRecord) => {
      if (isComlimentsOrMarket && selectedCards?.length >= sRecord?.length) {
        const unmatchedCompliments = selectedCards.filter(
          (compliment) =>
            !sRecord.some((record) => getCardSelectionId(record) === getCardSelectionId(compliment)),
        );

        const result = unmatchedCompliments.filter(
          (item) =>
            !complimentsList.some((record) => getCardSelectionId(record) === getCardSelectionId(item)),
        );

        if (result?.length > 0) {
          const tempResult = sRecord.concat(result);
          dispatch(setSelectedCards(tempResult));
          const recordIds = tempResult.map(getCardRowKey);
          setSelectedRowKeys(recordIds);
        } else {
          setSelectedRowKeys(sRowKeys);
          dispatch(setSelectedCards(sRecord));
        }
      } else {
        setSelectedRowKeys(sRowKeys);
        dispatch(setSelectedCards(sRecord));
      }
    },
    [dispatch, selectedCards, complimentsList],
  );

  const editableActionButtons = (record) => {
    if (isComlimentsOrMarket) {
      return (
        <div
          className="action-btn-active-cards-row"
          style={{ display: 'flex', flexDirection: 'row', gap: 10 }}
        >
          <button
            type="button"
            onClick={handleGiveUp}
            style={{ width: 80, borderWidth: 0, backgroundColor: 'transparent' }}
          >
            <span className="comp-editable-row-give-up-btn">
              <Text t="giveUp" />
            </span>
          </button>
          <button
            type="button"
            onClick={() => handleUpdate(record)}
            style={{
              width: 80,
              height: 36,
              borderRadius: 12,
              borderWidth: 0,
              backgroundColor: '#f66301',
            }}
          >
            <span className="comp-editable-row-give-up-btn-2">
              <Text t="saveBtn" />
            </span>
          </button>
        </div>
      );
    }
    return (
      <div className="edited-btn">
        {' '}
        <Typography.Link onClick={handleGiveUp} className={'editable-row-give-up-btn'}>
          <Text t="giveUp" />
        </Typography.Link>
        <Button
          id="manageMyCards-activeCardList-editable-row-update-btn"
          className="editable-row-update-btn"
          onClick={() => handleUpdate(record)}
        >
          <Text t="saveBtn" />
        </Button>
      </div>
    );
  };

  const noEditAddressRender = (deliveryAddress) => {
    return deliveryAddress || 'Adres Yok';
  };

  const confirmDialogMessage = useText('giveUpCancellationText');
  const filtersText = useText('filters');

  const cancelCancellation = async (record) => {
    if (isComlimentsOrMarket) {
      const htmlContent = (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span className="comp-warn-head ">
            <Text t="stopCardCancellation" />
          </span>
          <p className="comp-warn-text" style={{ marginBottom: 0 }}>
            <Text t="withThisTransactionYouWillStopTheCancellation" />
          </p>
          <p className="comp-warn-text" style={{ marginBottom: 0 }}>
            <Text t="youWillOpen" />
          </p>
          <p className="comp-warn-text">
            <Text t="areYouSureYouWantToContinue" />
          </p>
          <button
            type="button"
            style={{ marginTop: 20, width: 180 }}
            className="utts-ok-button comp-warn-confirm-ac"
            onClick={async () => {
              const action = await dispatch(cancelRequestsDelete({ cswId: record?.cswId }));
              if (cancelRequestsDelete.fulfilled.match(action)) {
                warningDialog({
                  title: <Text t="success" />,
                  width: 440,
                  notWarnIcon: true,
                  htmlContent: (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <div
                        style={{
                          color: '#027a48',
                          borderRadius: 12,
                          height: 56,
                          backgroundColor: '#d1fadf',
                          padding: 12,
                          display: 'flex',
                          marginBottom: 10,
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <CustomImage src={tickCircle} />
                        <span
                          className="comp-warn-head "
                          style={{
                            color: '#027a48',
                            textAlign: 'left',
                            marginLeft: 10,
                            fontSize: 16,
                            marginBottom: 0,
                          }}
                        >
                          <Text t="cardReactivated" />
                        </span>
                      </div>
                      <p className="comp-warn-text">
                        <Text t="theCancellationProcessOfYourCardHasBeen" />
                      </p>
                      <button
                        type="button"
                        style={{ marginTop: 20 }}
                        className="utts-ok-button comp-warn-confirm"
                        onClick={async () => null}
                      >
                        <Text t="okBtn" />
                      </button>
                    </div>
                  ),
                  className: 'warningModal-utts not-show-warning-buttons',
                  okButtonProps: 'not-show-warning-buttons',
                  onOk: async () => { },
                  onCancel: async () => { },
                });
                await dispatch(
                  cardGetPagedList({
                    body: { vouId: vouId },
                    ...filterObject,
                    pageNumber: isComlimentsOrMarket ? pagination.current : pageNumber,
                    pageSize: isComlimentsOrMarket ? pagination.pageSize : pageSize,
                  }),
                );
              }
            }}
          >
            <Text t="continue" />
          </button>
          <button
            type="button"
            className="utts-cancel-button gift-cancel-order-cancel"
            onClick={() => handleClose()}
          >
            <Text t="giveUp" />
          </button>
        </div>
      );
      warningDialog({
        title: '',
        width: 550,
        headerTransparent: true,
        notWarnIcon: true,
        htmlContent: htmlContent,
        nonPadding: false,
        className: 'warningModal-utts not-show-warning-buttons',
        okButtonProps: 'not-show-warning-buttons',
        onOk: async () => { },
        onCancel: async () => { },
      });
    } else {
      confirmDialog({
        title: <Text t="attention" />,
        message: confirmDialogMessage,
        onOk: async () => {
          const action = await dispatch(cancelRequestsDelete({ cswId: record?.cswId }));
          if (cancelRequestsDelete.fulfilled.match(action)) {
            successDialog({
              title: <Text t="success" />,
              message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
              onOk: async () => {
                await dispatch(
                  cardGetPagedList({
                    body: { vouId: vouId },
                    ...filterObject,
                    pageNumber: isComlimentsOrMarket ? pagination.current : pageNumber,
                    pageSize: isComlimentsOrMarket ? pagination.pageSize : pageSize,
                  }),
                );
              },
              onCancel: () => { },
            });
          }
        },
      });
    }
  };

  const createOnCell =
    ({ dataIndex, input, required = false, rules, inputType, editing = true, extraProps = {} }) =>
      (record) => ({
        record,
        dataIndex,
        editing: editing ? isEditing(record) : false,
        required,
        input,
        rules,
        inputType,
        ...extraProps,
      });

  const columnAD = {
    title: <Text t="nameUpperCase" />,
    dataIndex: 'name',
    key: 'name',
    width: 100,
    align: 'center',
    ellipsis: {
      showTitle: true,
    },
    onCell: createOnCell({
      dataIndex: 'name',
      input: (props) => getInput(props, 'Adı', false, 'name'),
    }),
    sortDirections: ['descend', 'ascend'],
  };
  const columnSOYAD = {
    title: <Text t="surnameUpperCase" />,
    dataIndex: 'surname',
    key: 'surname',
    width: 100,
    align: 'center',
    ellipsis: {
      showTitle: true,
    },
    onCell: createOnCell({
      dataIndex: 'surname',
      input: (props) => getInput(props, 'Soyadı', false, 'surname'),
    }),
    sortDirections: ['descend', 'ascend'],
  };
  const columnREFERANSKODU = {
    title: <Text t="referenceCode3" />,
    dataIndex: 'refNo',
    align: 'center',
    key: 'refNo',
    width: 100,
    onCell: createOnCell({
      dataIndex: 'refNo',
      required: true,
      input: (props) => getInput({ ...props, maxLength: 11 }, 'Maks.11 hane', false, 'refNo'),
    }),
  };
  const columnCEP = {
    title: <Text t="phoneNumberUpperCase" />,
    dataIndex: 'gsm',
    key: 'gsm',
    align: 'center',
    width: 100,
    onCell: (record) => ({
      record,
      input: GsmInputComponent,
      dataIndex: 'gsm',
      editing: isEditing(record),
    }),
  };
  const emailInput = (props) => <CustomInput {...props} height={36} />;
  const columnEMAIL = {
    title: <Text t="emailText" />,
    dataIndex: 'email',
    key: 'email',
    align: 'center',
    width: 150,
    onCell: createOnCell({
      dataIndex: 'email',
      input: emailInput,
      rules: formMailRegex,
    }),
  };

  const isSpecialCardType = activeCardListType === 4 || activeCardListType === 3;

  const complimentsSpecialColumns = [
    {
      title: <Text t="cardNumber4" />,
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      align: 'left',
      width: 140,
      onCell: createOnCell({
        dataIndex: 'cardNumber',
        input: (props) => getInput(props, 'Kart Numarası', false, 'cardNumber'),
        editing: false,
      }),
    },
    columnAD,
    columnSOYAD,
    columnREFERANSKODU,
    columnCEP,
    ...(isCompliments ? [columnEMAIL] : []),
    {
      title: <Text t="actionUpperCase" />,
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      width: 160,
      render: (_, record) => {
        if (activeCardListType === 4) {
          const renderActionButton = (record) => {
            if (record.statusId === 16) {
              return (
                <span className="comp-canceled-card-end-text">
                  <Text t="cancelled" />{' '}
                  <Tooltip
                    overlayClassName="compliment-cancelled-custom-tooltip"
                    title={
                      <div className="div-box">
                        <CustomImage src={iconWhiteInfo} />
                        <span style={{ color: '#fff', fontWeight: 'normal', fontSize: 12 }}>
                          <Text t="thisCardHasBeenPermanentlyCancelledYouCan" />
                        </span>
                      </div>
                    }
                    placement="bottomLeft"
                    color="#646363"
                    arrowPointAtCenter={true}
                  >
                    {' '}
                    <CustomImage src={iconInfo} />
                  </Tooltip>
                </span>
              );
            }

            if (
              handleAuthStatus(userAuthorizationStatusForType, 'ManageMyCardsCancelCancellation')
            ) {
              return null;
            }
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  id="manageMyCards-activeCardList-table-row-edit-btn"
                  onClick={() => cancelCancellation(record)}
                  disabled={editingKey !== ''}
                  style={{
                    backgroundColor: '#12b76a',
                    borderRadius: 12,
                    color: '#fff',
                    padding: 8,
                    paddingLeft: 12,
                    paddingRight: 12,
                    display: 'flex',
                    height: 36,
                    marginRight: 5,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: 0,
                  }}
                >
                  <Text t="cancelCancellation2" />
                </Button>

                <Tooltip
                  overlayClassName="compliment-cancelled-custom-tooltip"
                  title={
                    <div className="div-box">
                      <CustomImage src={iconWhiteInfo} />
                      <span style={{ color: '#fff', fontWeight: 'normal', fontSize: 12 }}>
                        <Text t="yourCardCancellationRequestHasNotBeenProcessed" />
                      </span>
                    </div>
                  }
                  placement="bottomLeft"
                  color="#646363"
                  arrowPointAtCenter={true}
                >
                  {' '}
                  <CustomImage src={iconInfo} />
                </Tooltip>
              </div>
            );
          };

          return (
            <div
              className="action-btn-active-cards-row"
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: 10,
              }}
            >
              {renderActionButton(record)}
            </div>
          );
        } else {
          return (
            <div
              className="action-btn-active-cards-row"
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: 10,
              }}
            >
              {!handleAuthStatus(userAuthorizationStatusForType, 'ManageMyCardsCancelBlock') && (
                <Button
                  id="manageMyCards-activeCardList-table-row-cancel-action-btn"
                  className={'table-row-cancel-action-btn'}
                  style={{
                    backgroundColor: '#1e293b',
                    borderRadius: 12,
                    color: '#fff',
                    padding: 8,
                    display: 'flex',
                    height: 36,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  disabled={editingKey !== ''}
                  onClick={() => handleCancelProceed(record)}
                >
                  <Text t="transactions2" />
                </Button>
              )}
            </div>
          );
        }
      },
    },
  ];

  const complimentsNormalColumns = [
    {
      title: <Text t="rowUpperCase" />,
      dataIndex: 'rowNumber',
      key: 'rowNumber',
      fixed: 'left',
      align: 'center',
      width: 60,
    },
    {
      title: <Text t="cardNumber4" />,
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      align: 'center',
      width: 140,
      onCell: createOnCell({
        dataIndex: 'cardNumber',
        input: (props) => getInput(props, 'Kart Numarası', false, 'cardNumber'),
        editing: false,
      }),
    },
    columnAD,
    columnSOYAD,
    columnREFERANSKODU,
    columnCEP,
    ...(isCompliments ? [columnEMAIL] : []),
    {
      title: <Text t="deliveryAddress" />,
      dataIndex: 'addressId',
      key: 'addressId',
      width: 120,
      align: 'center',
      onCell: createOnCell({
        dataIndex: 'addressId',
        input: AddressIdComponent(addressPagedList),
        required: true,
      }),
      render: (_, record) => {
        return noEditAddressRender(record?.addressName);
      },
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: <Text t="linkedToMyEdenred" />,
      dataIndex: 'mobileAppUserId',
      key: 'mobileAppUserId',
      width: 110,
      align: 'center',
      onCell: (record) => ({
        record,
        input: HasMobileAppComponent(record),
        dataIndex: 'mobileAppUserId',
        editing: isEditing(record),
        inputType: 'checkbox',
      }),
      render: (text, record) => (
        <span>{record.mobileAppUserId ? <CustomImage src={iconChecked} alt="check" /> : ''}</span>
      ),
    },

    {
      title: <Text t="cardType" />,
      dataIndex: 'cardType',
      key: 'cardType',
      width: 75,
      align: 'center',
      onCell: (record) =>
        createOnCell({
          dataIndex: 'cardType',
          editing: false,
        }),
      render: (text, record) => <span>{record?.cardType ? <Text t="digital" /> : <Text t="physical" />}</span>,
    },

    {
      title: <Text t="mobilePayment" />,
      dataIndex: 'hasMobilePayment',
      key: 'hasMobilePayment',
      width: 75,
      align: 'center',
      onCell: (record) => ({
        record,
        input: HasMobilePaymentComponent(record),
        dataIndex: 'hasMobilePayment',
        editing: isEditing(record),
        inputType: 'checkbox',
      }),
      render: (text, record) => (
        <span>{record.hasMobilePayment ? <CustomImage src={iconChecked} alt="check" /> : ''}</span>
      ),
    },

    {
      title: <Text t="actionUpperCase" />,
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      width: 200,
      render: (_, record) => {
        const editable = isEditing(record);
        if (editable) {
          return editableActionButtons(record);
        }
        return (
          <div
            className="action-btn-active-cards-row"
            style={{ display: 'flex', flexDirection: 'row', gap: 10 }}
          >
            {!handleAuthStatus(userAuthorizationStatusForType, 'ManageMyCardsCancelBlock') && (
              <Button
                id="manageMyCards-activeCardList-table-row-cancel-action-btn"
                className={'table-row-cancel-action-btn'}
                style={{
                  backgroundColor: '#1e293b',
                  borderRadius: 12,
                  color: '#fff',
                  padding: 8,
                  display: 'flex',
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                disabled={editingKey !== ''}
                onClick={() => handleCancelProceed(record)}
              >
                <Text t="transactions2" />
              </Button>
            )}
            {!handleAuthStatus(userAuthorizationStatusForType, 'ManageMyCardsEdit') && (
              <Button
                id="manageMyCards-activeCardList-table-row-edit-btn"
                onClick={() => handleEdit(record)}
                disabled={editingKey !== ''}
                style={{
                  backgroundColor: '#00a483',
                  borderRadius: 12,
                  color: '#fff',
                  padding: 8,
                  display: 'flex',
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text t="edit" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];
  const marketColumns = [
    {
      title: <Text t="cardNumber4" />,
      dataIndex: 'cardNumber',
      key: 'cardNumber',
      width: 140,
      onCell: createOnCell({
        dataIndex: 'cardNumber',
        input: (props) => getInput(props, 'Kart Numarası', false, 'cardNumber'),
        editing: false,
      }),
      sorter: (a, b) => {
        const numA = parseFloat(a.cardNumber);
        const numB = parseFloat(b.cardNumber);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }

        return 0;
      },
    },
    {
      title: <Text t="nameUpperCase" />,
      dataIndex: 'name',
      key: 'name',
      width: 100,
      ellipsis: {
        showTitle: true,
      },
      onCell: createOnCell({
        input: (props) => getInput(props, 'Adı', true, 'name'),
        dataIndex: 'name',
        required: true,
      }),
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: <Text t="surnameUpperCase" />,
      dataIndex: 'surname',
      key: 'surname',
      width: 100,
      ellipsis: {
        showTitle: true,
      },
      onCell: createOnCell({
        input: (props) => getInput(props, 'Soyadı', true, 'surname'),
        dataIndex: 'surname',
        required: true,
      }),
      sorter: (a, b) => a.surname.localeCompare(b.surname),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: <Text t="referenceCode3" />,
      dataIndex: 'refNo',
      key: 'refNo',
      width: 100,
      onCell: createOnCell({
        input: (props) => getInput({ ...props, maxLength: 11 }, 'Maks.11 hane', true, 'refNo'),
        dataIndex: 'refNo',
        required: true,
      }),
      sorter: (a, b) => {
        const numA = parseFloat(a.refNo);

        const numB = parseFloat(b.refNo);

        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return a.refNo.localeCompare(b.refNo);
      },
    },
    {
      title: <Text t="mobilePhoneUpperCase" />,
      dataIndex: 'gsm',
      key: 'gsm',
      width: 100,
      onCell: (record) => ({
        record,
        input: GsmInputComponent,
        dataIndex: 'gsm',

        editing: isEditing(record),
      }),
      sorter: (a, b) => {
        const numA = parseFloat(a.gsm);
        const numB = parseFloat(b.gsm);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }

        return 0;
      },
    },
    {
      title: <Text t="deliveryAddressUpperCase" />,
      dataIndex: 'addressId',
      key: 'addressId',
      width: 120,
      onCell: createOnCell({
        input: AddressIdComponent(addressPagedList),
        dataIndex: 'addressId',
        required: true,
      }),
      render: (_, record) => {
        return noEditAddressRender(record?.addressName);
      },
      sorter: (a, b) => {
        const addressIdA = a.addressId ? String(a.addressId) : '';
        const addressIdB = b.addressId ? String(b.addressId) : '';
        return addressIdA.localeCompare(addressIdB);
      },
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: <Text t="linkedToMyEdenred" />,
      dataIndex: 'mobileAppUserId',
      key: 'mobileAppUserId',
      width: 110,
      onCell: (record) => ({
        record,
        input: HasMobileAppComponent(record),
        dataIndex: 'mobileAppUserId',
        editing: isEditing(record),
        inputType: 'checkbox',
      }),
      render: (text, record) => (
        <span>{record.mobileAppUserId ? <CustomImage src={iconChecked} alt="check" /> : ''}</span>
      ),
    },

    {
      title: <Text t="cardType" />,
      dataIndex: 'cardType',
      key: 'cardType',
      width: 75,
      onCell: createOnCell({
        dataIndex: 'cardType',
        editing: false,
      }),

      render: (text, record) => <span>{record?.cardType ? <Text t="digital" /> : <Text t="physical" />}</span>,

      sorter: (a, b) => {
        if (a.cardType === b.cardType) return 0;
        return a.cardType ? -1 : 1;
      },
    },

    {
      title: <Text t="mobilePayment" />,
      dataIndex: 'hasMobilePayment',
      key: 'hasMobilePayment',
      width: 75,
      onCell: (record) => ({
        record,
        input: HasMobilePaymentComponent(record),
        dataIndex: 'hasMobilePayment',
        editing: isEditing(record),
        inputType: 'checkbox',
      }),
      render: (text, record) => (
        <span>{record.hasMobilePayment ? <CustomImage src={iconChecked} alt="check" /> : ''}</span>
      ),
    },

    {
      title: <Text t="actionUpperCase" />,
      dataIndex: 'action',
      key: 'action',
      width: 130,
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          editableActionButtons(record)
        ) : (
          <div className="action-btn-active-cards-row">
            {!handleAuthStatus(userAuthorizationStatusForType, 'ManageMyCardsCancelBlock') && (
              <Button
                id="manageMyCards-activeCardList-table-row-cancel-action-btn"
                className={'table-row-cancel-action-btn'}
                disabled={editingKey !== ''}
                onClick={() => handleCancelProceed(record)}
              >
                <Text t="cancel2" />
              </Button>
            )}
            {!handleAuthStatus(userAuthorizationStatusForType, 'ManageMyCardsEdit') && (
              <Button
                id="manageMyCards-activeCardList-table-row-edit-btn"
                className={'table-row-edit-btn'}
                onClick={() => handleEdit(record)}
                disabled={editingKey !== ''}
              >
                <Text t="edit" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  let columns;

  if (isComlimentsOrMarket) {
    columns = isSpecialCardType ? complimentsSpecialColumns : complimentsNormalColumns;
  } else {
    columns = marketColumns;
  }

  const clickComplimentsTopButtons = async (val) => {
    setActiveCardListType(val);
    let statusId;
    if (val === 3) {
      statusId = 50;
    } else if (val === 4) {
      statusId = 60;
    } else {
      statusId = null;
    }

    await dispatch(
      cardGetPagedList({
        ...filterObject,
        body: { vouId: vouId, statusId: statusId },
        sortParams: sortRef.current,
      }),
    );
  };

  const handleTablePaginationChange = async (pageNumber, pageSize) => {
    await dispatch(setLoadingState(true));
    setPagination((prevPagination) => ({
      ...prevPagination,
      current: pageNumber,
    }));
    await dispatch(
      cardGetPagedList({
        body: { vouId: vouId },
        ...filterObject,
        pageNumber: pageNumber,
        pageSize: pagination.pageSize,
        sortParams: sortRef.current,
      }),
    );
    handleGiveUp();
    await dispatch(setLoadingState(false));
  };

  const getBorderBottomWidth = (val) => {
    return activeCardListType === val ? 2 : 1;
  };

  const getBorderBottomColor = (val) => {
    return activeCardListType === val ? '#006cfa' : '#d0d6e0';
  };

  if (isComlimentsOrMarket) {
    return (
      <div className="compliment-active-card-list-card">
        <CustomCollapseCard
          cardTitle={filtersText}
          className="compliment-active-card-list-card"
        >
          <CardFilter
            activeCardListType={activeCardListType}
            isCompliments={isCompliments}
            isMarket={isMarket}
            resetPagination={() =>
              setPagination({
                current: 1,
                pageSize: 100,
              })
            }
          />
        </CustomCollapseCard>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            marginBottom: 20,
            gap: 10,
            justifyContent: 'flex-end',
          }}
        >
          {!handleAuthStatus(userAuthorizationStatusForType, 'ManageMyCardsEdit') && (
            <button
              type="button"
              id="ticketRestaurant-manageMyCards-activeCardList-mobileqr-update-btn"
              onClick={() => {
                if (selectedRowKeys.length < 1) {
                  warningDialog({
                    title: <Text t="attention" />,
                    message: pleaseSelectCard,
                  });
                } else {
                  setQrMobileChangeShow(true);
                }
              }}
              disabled={activeCardListType !== 2}
              className="comp-mobileqr-update-btn"
              style={{ width: 203 }}
            >
              <Text t="changeMobilePaymentPermission2" />
            </button>
          )}
          {isCompliments &&
            !handleAuthStatus(userAuthorizationStatusForType, 'ManageMyCardsEdit') && (
            <button
              id="ticketRestaurant-manageMyCards-activeCardList-send-eac-btn"
              type="button"
              disabled={activeCardListType !== 2}
              className="comp-mobileqr-update-btn send-eac-btn"
              onClick={() => handleSendEac()}
            >
              <Text t="sendEac" />
              <Tooltip
                title={<Text t="sendEacTooltip" />}
                placement="top"
                overlayClassName="send-eac-tooltip"
              >
                <span style={{ display: 'inline-flex' }}>
                  <CustomImage src={eacInfoIcon} alt="info" style={{ width: 24, height: 24 }} />
                </span>
              </Tooltip>
            </button>
            )}
          {!handleAuthStatus(userAuthorizationStatusForType, 'ManageMyCardsEdit') && (
            <button
              type="button"
              id="ticketRestaurant-manageMyCards-activeCardList-address-update-btn"
              className="comp-mobileqr-update-btn"
              onClick={() => {
                if (selectedRowKeys.length < 1) {
                  warningDialog({
                    title: <Text t="attention" />,
                    message: pleaseSelectCard,
                  });
                } else {
                  setDeliverAddressModalVisible(true);
                }
              }}
              disabled={activeCardListType !== 2}
              style={{ width: 163 }}
            >
              <CustomImage src={iconCompAddress} />
              <Text t="updateAddress" />
            </button>
          )}
          {!handleAuthStatus(userAuthorizationStatusForType, 'ManageMyCardsCancelBlock') && (
            <button
              type="button"
              id="ticketRestaurant-manageMyCards-activeCardList-card-cancel-btn"
              className="comp-mobileqr-update-btn"
              onClick={() => {
                if (selectedRowKeys.length < 1) {
                  warningDialog({
                    title: <Text t="attention" />,
                    message: pleaseSelectCard,
                  });
                } else {
                  setBlockedModal2Visible(true);
                  setBlockedModalVisible(true);
                }
              }}
              disabled={activeCardListType !== 2}
              style={{ width: 91 }}
            >
              <Text t="transactions" />
            </button>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            backgroundColor: '#fff',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            height: 64,
            paddingLeft: 20,
            paddingRight: 10,
          }}
        >
          <span className="compliment-card-list-text">
            <Text t="cardList" />
          </span>

          <button
            type="button"
            onClick={() => clickComplimentsTopButtons(2)}
            style={{
              backgroundColor: 'transparent',
              borderWidth: 0,
              width: 124,
              borderBottomWidth: getBorderBottomWidth(2),
              borderBottomColor: getBorderBottomColor(2),
              height: 44,
            }}
          >
            <span
              className={
                activeCardListType === 2
                  ? 'compliment-card-list-other-text-active'
                  : 'compliment-card-list-other-text'
              }
            >
              <Text t="activeCards" />
            </span>
          </button>
          <button
            type="button"
            onClick={() => clickComplimentsTopButtons(3)}
            style={{
              backgroundColor: 'transparent',
              borderWidth: 0,
              width: 147,
              borderBottomWidth: getBorderBottomWidth(3),
              borderBottomColor: getBorderBottomColor(3),
              height: 44,
            }}
          >
            <span
              className={
                activeCardListType === 3
                  ? 'compliment-card-list-other-text-active'
                  : 'compliment-card-list-other-text'
              }
            >
              <Text t="temporarilyBlocked" />
            </span>
          </button>
          <button
            type="button"
            onClick={() => clickComplimentsTopButtons(4)}
            style={{
              backgroundColor: 'transparent',
              borderWidth: 0,
              width: 134,
              borderBottomWidth: getBorderBottomWidth(4),
              borderBottomColor: getBorderBottomColor(4),
              height: 44,
            }}
          >
            <span
              className={
                activeCardListType === 4
                  ? 'compliment-card-list-other-text-active'
                  : 'compliment-card-list-other-text'
              }
            >
              <Text t="cancelledOnes" />
            </span>
          </button>
        </div>
        <Form form={form} component={false}>
          <VirtualizedAndLazyScrollTable
            componentsData={{
              body: {
                cell: EditableCell,
              },
            }}
            pagination={false}
            rowSelection={
              activeCardListType === 4 || activeCardListType === 3
                ? null
                : {
                  selectedRowKeys,
                  onChange: onSelectChange,
                }
            }
            dataSource={complimentsList}
            columnsData={columns}
            rowKey={getCardRowKey}
          />
        </Form>

        <DeliveryAddressUpdateModal
          modalVisible={deliverAddressModalVisible}
          isCompliments={isCompliments}
          isMarket={isMarket}
          handleModalVisible={async (visible) => {
            setDeliverAddressModalVisible(visible);
            setSelectedRowKeys([]);
            await dispatch(setIsAllSelectedRowKeys(false));
            dispatch(setSelectedCards([]));
          }}
        />

        <EacContactUpdateModal
          cards={eacContactCards}
          modalVisible={eacContactCards.length > 0}
          vouId={vouId}
          handleModalVisible={(visible) => {
            if (!visible) setEacContactCards([]);
          }}
          onContactSaved={async (updatedCards) => {
            const selectedCardsWithContactInfo = mergeEacContactUpdates(selectedCards, updatedCards);
            dispatch(setSelectedCards(selectedCardsWithContactInfo));
            await handleSendEac(selectedCardsWithContactInfo);
          }}
        />

        {eacConfirmationCards.length > 0 && (
          <Modal
            className="eac-send-confirmation-modal"
            visible
            width={560}
            centered
            footer={null}
            title={null}
            maskClosable={false}
            onCancel={() => setEacConfirmationCards([])}
          >
            <div className="eac-send-confirmation-content">
              <h2>{eacConfirmTitle}</h2>
              <p className="eac-send-confirmation-description">
                {eacConfirmationCards.length === 1 ? (
                  <>
                    {eacConfirmDescriptionSingular
                      .split('{cardNumber}')
                      .map((textPart, index) => (
                        <React.Fragment key={index}>
                          {index > 0 && <strong>{maskEacCardNumber(eacConfirmationCards[0])}</strong>}
                          {textPart}
                        </React.Fragment>
                      ))}
                  </>
                ) : (
                  <>
                    <span>{eacConfirmSelectedLabel}</span>{' '}
                    <strong>
                      {eacConfirmSelectedCards.replace('{count}', eacConfirmationCards.length)}
                    </strong>{' '}
                    {eacConfirmDescriptionPlural}
                  </>
                )}
              </p>
              <p className="eac-send-confirmation-prompt">{eacConfirmPrompt}</p>
              <Button
                className="eac-send-confirmation-submit"
                type="primary"
                onClick={handleConfirmEacSend}
              >
                {eacConfirmButton}
              </Button>
              <Button
                className="eac-send-confirmation-cancel"
                type="text"
                onClick={() => setEacConfirmationCards([])}
              >
                {cancelButton}
              </Button>
            </div>
          </Modal>
        )}

        <BlockedModal
          modalVisible={blockedModalVisible}
          blockedModal2Visible={blockedModal2Visible}
          isCompliments={isCompliments}
          isMarket={isMarket}
          handleModalVisible={async (visible) => {
            setBlockedModalVisible(visible);
            setBlockedModal2Visible(visible);
            setSelectedRowKeys([]);
            await dispatch(setIsAllSelectedRowKeys(false));
            dispatch(setSelectedCards([]));
          }}
        />
        <MobilePaymentChangeModal
          modalVisible={qrMobileChangeShow}
          isCompliments={isCompliments}
          isMarket={isMarket}
          handleModalVisible={async (visible) => {
            setQrMobileChangeShow(visible);
            setSelectedRowKeys([]);
            await dispatch(setIsAllSelectedRowKeys(false));
            dispatch(setSelectedCards([]));
          }}
          selectedRowKeys={selectedRowKeys}
        />
        {tableProperty?.totalItems > 100 && (
          <div
            className="d-flex"
            style={{
              position: 'relative',
              backgroundColor: '#fff',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
            }}
          >
            <CustomPagination
              current={tableProperty?.currentPage}
              defaultCurrent={1}
              pageSize={tableProperty?.pageSize}
              total={tableProperty?.totalItems}
              onChange={async (pageNumber, pageSize) => {
                await handleTablePaginationChange(pageNumber, pageSize);
              }}
            />
          </div>
        )}
      </div>
    );
  }
  return (
    <CustomCollapseCard cardTitle={cardListText} className="active-card-list-card">
      <CardFilter />
      <Form form={form} component={false}>
        <VirtualizedAndLazyScrollTable
          componentsData={{
            body: {
              cell: EditableCell,
            },
          }}
          pagination={false}
          rowSelection={{
            selectedRowKeys,
            onChange: onSelectChange,
          }}
          getDataFunc={async (_, pageSize) => {
            await handleTableChange(1, undefined);
          }}
          dataSource={list}
          columnsData={columns}
          rowKey={(record) => `manage-my-card-${record?.id}`}
          onChange={handleTableSortedChange}
        />
      </Form>

      <div className="card-list-table-footer">
        <div />
        <div className="footer-info-container">
          <CustomButton
            id="ticketRestaurant-manageMyCards-activeCardList-mobileqr-update-btn"
            data-testid="mobileqr-update-btn"
            onClick={() => {
              setQrMobileChangeShow(true);
            }}
            disabled={selectedRowKeys.length < 1}
            className="mobileqr-update-btn"
            buttonAuthName="ManageMyCardsEdit"
          >
            <Text t="changeMobilePaymentPermission" />
          </CustomButton>
          <CustomButton
            id="ticketRestaurant-manageMyCards-activeCardList-address-update-btn"
            className="address-update-btn"
            data-testid="address-update-btn"
            disabled={selectedRowKeys.length < 1}
            onClick={() => setDeliverAddressModalVisible(true)}
            buttonAuthName="ManageMyCardsEdit"
          >
            <CustomImage src={iconAddress} />
            <Text t="updateAddress2" />
          </CustomButton>
          <CustomButton
            id="ticketRestaurant-manageMyCards-activeCardList-card-cancel-btn"
            className="card-cancel-btn"
            disabled={selectedRowKeys.length < 1}
            onClick={() => setBlockedModalVisible(true)}
            buttonAuthName="ManageMyCardsCancelBlock"
          >
            <CustomImage src={iconCancelCard} />
            <Text t="cardCancellation" />
          </CustomButton>
        </div>
      </div>

      <DeliveryAddressUpdateModal
        modalVisible={deliverAddressModalVisible}
        handleModalVisible={async (visible) => {
          setDeliverAddressModalVisible(visible);
          setSelectedRowKeys([]);
          await dispatch(setIsAllSelectedRowKeys(false));
          dispatch(setSelectedCards([]));
        }}
      />

      <BlockedModal
        modalVisible={blockedModalVisible}
        handleModalVisible={async (visible) => {
          setBlockedModalVisible(visible);
          setSelectedRowKeys([]);
          await dispatch(setIsAllSelectedRowKeys(false));
          dispatch(setSelectedCards([]));
        }}
      />
      <MobilePaymentChangeModal
        modalVisible={qrMobileChangeShow}
        handleModalVisible={async (visible) => {
          setQrMobileChangeShow(visible);
          setSelectedRowKeys([]);
          await dispatch(setIsAllSelectedRowKeys(false));
          dispatch(setSelectedCards([]));
        }}
        selectedRowKeys={selectedRowKeys}
      />
    </CustomCollapseCard>
  );
};
ActiveCardList.propTypes = {
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
};

export default ActiveCardList;
